import {
  BlockStack,
  Button,
  FormLayout,
  InlineGrid,
  InlineStack,
  LegacyStack,
  Tag,
  Text,
} from '@shopify/polaris'
import { useCallback, useEffect, useState } from 'react'
import { mutate } from 'swr'

import { AdaptiveModal } from '../common/AdaptiveModal'
import { AnchorsEditModal, anchorTypeSellingPlanIntervals } from '../common/AnchorsEditModal'
import { backendFetchResult, Result } from '../common/api'
import {
  DialogActivator,
  ResultBanner,
  ResultToast,
  sortAnchors,
  useDialogActivator,
} from '../common/helpers'
import { SellingPlanInterval } from '../common/plan-models'
import {
  IntervalCountField,
  IntervalField,
  MaxCyclesField,
  MinCyclesField,
} from '../common/ScheduleFields'
import { FinishingBehaviorSelect } from '../common/subscription-fields'
import {
  defaultFinishingBehavior,
  FinishingBehavior,
  SubscriptionContract,
} from '../common/subscription-models'
import * as urls from '../common/urls'
import { PrePaidDeliveriesField } from '../selling-plan-page/PrePaidDeliveriesField'
import { anchorLabel } from './ScheduleCard'

interface Props {
  activator: DialogActivator
  sc: SubscriptionContract
}

export const ScheduleEditDialog = ({ activator, sc }: Props) => {
  const editAnchorsActivator = useDialogActivator()

  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)

  const [deliveryIntervalCount, setDeliveryIntervalCount] = useState<number>(
    sc.gql_data.deliveryPolicy.intervalCount
  )
  const [prePaidDeliveries, setPrePaidDeliveries] = useState<number>(
    Math.round(sc.gql_data.billingPolicy.intervalCount / sc.gql_data.deliveryPolicy.intervalCount)
  )
  const billingIntervalCount = deliveryIntervalCount * prePaidDeliveries

  const [interval, setInterval] = useState<SellingPlanInterval>(sc.gql_data.billingPolicy.interval)
  const [minCycles, setMinCycles] = useState<number | null>(sc.gql_data.billingPolicy.minCycles)
  const [maxCycles, setMaxCycles] = useState<number | null>(sc.gql_data.billingPolicy.maxCycles)
  const [anchors, setAnchors] = useState(sortAnchors(sc.gql_data.deliveryPolicy.anchors ?? []))

  const [finishingBehavior, setFinishingBehavior] = useState<FinishingBehavior>(
    sc.pw_data.finishingBehavior ?? defaultFinishingBehavior
  )

  useEffect(() => {
    setSaveResult(null)
    setDeliveryIntervalCount(sc.gql_data.deliveryPolicy.intervalCount)
    setPrePaidDeliveries(
      Math.round(sc.gql_data.billingPolicy.intervalCount / sc.gql_data.deliveryPolicy.intervalCount)
    )
    setInterval(sc.gql_data.billingPolicy.interval)
    setMinCycles(sc.gql_data.billingPolicy.minCycles)
    setMaxCycles(sc.gql_data.billingPolicy.maxCycles)
    setAnchors(sc.gql_data.deliveryPolicy.anchors ?? [])
    setFinishingBehavior(sc.pw_data.finishingBehavior ?? defaultFinishingBehavior)
  }, [sc, activator.showCounter])

  // Clear field error on change
  useEffect(() => {
    setSaveResult(null)
  }, [maxCycles])

  const save = useCallback(async () => {
    setSaving(true)

    const opts = {
      body: JSON.stringify({
        input: {
          billingPolicy: {
            anchors,
            intervalCount: billingIntervalCount,
            interval,
            minCycles,
            maxCycles,
          },
          deliveryPolicy: {
            anchors,
            intervalCount: deliveryIntervalCount,
            interval,
          },
        },
        pwData: {
          finishingBehavior,
        },
      }),
    }
    const resp = await backendFetchResult('PUT', `/subscriptions/${sc.rest_id}`, opts)

    setSaving(false)
    mutate(['subscription-contract', `${sc.rest_id}`])

    setSaveResult(resp)

    if (resp.status === 'success') {
      activator.close()
    }
  }, [
    activator,
    setSaving,
    sc,
    anchors,
    deliveryIntervalCount,
    billingIntervalCount,
    interval,
    minCycles,
    maxCycles,
    finishingBehavior,
  ])

  return (
    <>
      <AdaptiveModal
        open={activator.open && !editAnchorsActivator.open}
        title="Edit schedule"
        onClose={activator.close}
        primaryAction={{ content: 'Save', onAction: save, loading: saving }}
        secondaryActions={[{ content: 'Cancel', onAction: activator.close }]}
        sectioned
      >
        <FormLayout>
          <ResultBanner result={saveResult} setResult={setSaveResult} bottomMargin={false} />
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <InlineStack blockAlign="end" gap="200">
              <LegacyStack.Item fill>
                <InlineStack align="space-evenly" gap="200">
                  {anchors.length <= 1 && (
                    <LegacyStack.Item fill>
                      <IntervalCountField
                        label="Delivery frequency"
                        value={deliveryIntervalCount}
                        onChange={setDeliveryIntervalCount}
                      />
                    </LegacyStack.Item>
                  )}
                  <LegacyStack.Item fill>
                    <IntervalField
                      label={anchors.length > 1 ? 'Delivery frequency' : '  '}
                      value={interval}
                      onChange={(val: SellingPlanInterval) => {
                        setInterval(val)
                        setAnchors([])
                      }}
                      hasMultipleAnchors={anchors.length > 1}
                    />
                  </LegacyStack.Item>
                </InlineStack>
              </LegacyStack.Item>
            </InlineStack>
            <PrePaidDeliveriesField
              value={prePaidDeliveries}
              onChange={setPrePaidDeliveries}
              pagePath={urls.subscriptionUrl(sc.rest_id)}
            />
          </InlineGrid>
          {anchors.length === 0 && interval !== 'DAY' && (
            <Button onClick={editAnchorsActivator.show} variant="primary">
              Add delivery anchors
            </Button>
          )}
          {anchors.length > 0 && (
            <BlockStack gap="300">
              <InlineStack>
                <LegacyStack.Item fill>
                  <Text as="span">Delivery anchors</Text>
                </LegacyStack.Item>
                <Button onClick={editAnchorsActivator.show} variant="plain">
                  Edit delivery anchors
                </Button>
              </InlineStack>
              <InlineStack wrap gap="100">
                {anchors.map((anchor, i) => (
                  <Tag key={i}>{anchorLabel(anchor)}</Tag>
                ))}
              </InlineStack>
            </BlockStack>
          )}
          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <MinCyclesField value={minCycles} onChange={setMinCycles} />
            <MaxCyclesField
              value={maxCycles}
              onChange={setMaxCycles}
              error={saveResult?.field === 'maxCycles' ? saveResult.message : false}
            />
          </InlineGrid>
          <FinishingBehaviorSelect onChange={setFinishingBehavior} value={finishingBehavior} />
        </FormLayout>
      </AdaptiveModal>
      {editAnchorsActivator.open && (
        <AnchorsEditModal
          activator={editAnchorsActivator}
          anchors={anchors}
          interval={interval}
          onChange={(anchors, preAnchorBehavior, cutoff) => {
            setAnchors(sortAnchors(anchors))
            if (anchors.length > 0) {
              setDeliveryIntervalCount(1)
              setInterval(anchorTypeSellingPlanIntervals[anchors[0].type])
            }
          }}
          pagePath={urls.subscriptionUrl(sc.gid)}
        />
      )}

      <ResultToast result={saveResult} setResult={setSaveResult} />
    </>
  )
}
